import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Device } from "@/types/device";
import { useState, useMemo } from "react";
import * as devicesApi from "@/services/devicesApi";
import { getTenantId } from "@/services/apiClient";
import { getTagsByDevice } from "@/services/tagsApi";
import { getCustomerName } from "@/utils/customerLookup";

export type { Device, InstalledApp } from "@/types/device";

export const DEVICES_QUERY_KEY = ['devices'];

export function normalizeStatus(status?: unknown): string {
  if (!status) return 'unknown';
  const value = String(status);
  switch (value.toUpperCase()) {
    case 'ENROLLED': return 'active';
    case 'UNENROLLED': return 'inactive';
    case 'ENROLL_PENDING': return 'pending';
    default: return value.toLowerCase();
  }
}

function epochToIso(epoch?: number): string {
  if (!epoch) return '';
  // API returns seconds (e.g. 1775409289.827328)
  return new Date(epoch * 1000).toISOString();
}

export const useDevices = (params: { page?: number; pageSize?: number } = {}) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(params.page || 1);
  const [pageSize] = useState(params.pageSize || 25);

  const tenantId = getTenantId();

  const query = useQuery({
    queryKey: [...DEVICES_QUERY_KEY, page, pageSize],
    queryFn: async () => {
      if (!tenantId) {
        throw new Error('Your session is missing tenant information. Please sign in again.');
      }
      const res = await devicesApi.listDevices(page - 1, pageSize);

      // Normalize: res is the full ResponseDTO envelope
      let rawList: any[] = [];
      const resData = (res as any)?.data;
      if (Array.isArray(resData)) {
        rawList = resData;
      } else if (resData?.content && Array.isArray(resData.content)) {
        rawList = resData.content;
      } else if (Array.isArray(res)) {
        rawList = res;
      } else {
        
        rawList = [];
      }

      const safeText = (value: unknown): string => typeof value === "string" ? value : value == null ? "" : String(value);

      const inferPlatform = (d: any): string => {
        const platform = safeText(d.platform || d.osType || d.os_type).toLowerCase();
        if (platform) return platform;
        const product = safeText(d.product || d.modelName).toLowerCase();
        if (product.includes('iphone') || product.includes('ipad') || product.includes('ipod')) return 'ios';
        if (product.includes('mac') || product.includes('imac')) return 'macos';
        if (product.includes('appletv')) return 'tvos';
        return '';
      };

      const devices: Device[] = rawList.map((d: any) => ({
        id: d.uuid || d.id,
        name: d.name || d.deviceName || 'Unknown Device',
        os_type: inferPlatform(d),
        os_version: d.osVersion || d.os || d.os_version || '',
        status: normalizeStatus(d.status),
        last_seen: epochToIso(d.lastConnectedAt) || d.lastSeen || d.last_seen || '',
        created_at: d.createdAt || d.created_at || '',
        hostname: d.hostname || '',
        battery_level: d.batteryLevel ?? null,
        battery_status: d.batteryStatus ?? null,
        ip_address: d.ipAddress ?? null,
        serial_number: d.serialNumber || null,
        installed_apps: [],
        department: null,
        location: null,
        customer: d.tenantName || (d.tenantId ? getCustomerName(d.tenantId) : null),
        tenantId: d.tenantId || null,
        model: d.model || d.modelName || null,
        modelName: d.modelName || null,
        product: d.product || null,
        platform: d.platform || null,
        username: d.username || null,
        fullName: d.fullName || null,
        userEmail: d.userEmail || null,
        userId: d.userId || null,
      }));

      const pagination = (res as any)?.meta?.pagination || resData?.pagination;
      const total = pagination?.totalElements || devices.length;
      const totalPages = pagination?.totalPages || 1;
      return { devices, total, totalPages };
    },
    staleTime: 1000 * 30,
    refetchOnMount: true,
    enabled: !!tenantId,
  });

  const devices = query.data?.devices || [];
  const statusCounts = devices.reduce(
    (acc, d) => {
      acc.total++;
      if (d.status === 'active') acc.active++;
      else if (d.status === 'inactive') acc.inactive++;
      else if (d.status === 'pending') acc.pending++;
      return acc;
    },
    { active: 0, inactive: 0, pending: 0, total: 0 }
  );

  const deviceIds = useMemo(() => devices.map(d => d.id), [devices]);

  const tagsQuery = useQuery({
    queryKey: ['device-tags-bulk', deviceIds],
    queryFn: async () => {
      const map = new Map<string, { id: string; name: string; color: string }[]>();
      const results = await Promise.allSettled(
        deviceIds.map(id => getTagsByDevice(id))
      );
      results.forEach((result, idx) => {
        if (result.status === 'fulfilled' && result.value?.length) {
          map.set(deviceIds[idx], result.value.map((t: any) => ({
            id: t.tag_id || t.id || '',
            name: t.tag_name || t.name || '',
            color: t.tag_color || t.color || '#6B7280',
          })));
        }
      });
      return map;
    },
    enabled: deviceIds.length > 0,
    staleTime: 1000 * 60,
  });

  return {
    ...query,
    data: devices,
    total: query.data?.total || 0,
    page,
    pageSize,
    totalPages: query.data?.totalPages || 0,
    deviceTags: tagsQuery.data || new Map(),
    statusCounts,
    setPage,
  };
};

export const getTenantInfo = (_deviceId: string) => ({
  customer: '',
  tenantId: '',
});

export const invalidateDevicesCache = () => {
  if (typeof window !== 'undefined') {
    const event = new Event('device-cache-updated');
    window.dispatchEvent(event);
  }
};
